import {
	type ActorRefFrom,
	type ActorRefFromLogic,
	assign,
	fromPromise,
	sendTo,
	setup,
	waitFor,
} from "xstate";

import { type AuthenticationMachine, authnStateNotUnknown } from "~/state/machines/authentication";
import { isPublishedPromise } from "~/state/machines/docsapi/isPublished.machine";
import { remoteDocumentRepoExistsPromise } from "~/state/machines/docsapi/remoteDocumentRepoExists.machine";
import type { BranchInfo as GitBranchInfo } from "~/state/machines/git/gitBranches.machine";
import { type gitRepositoryMachine } from "~/state/machines/git/repository/git-repository.machine";
import type {
	GitRemote,
	GitRepoStatus,
	GitRepositoryMachineEmitted,
} from "~/state/machines/git/repository/git-repository.types";
import { hasPublishPermissionPromise } from "~/state/machines/permissions";

import { type SyncPlan, type SyncPlanMaybe, syncPlan } from "./sync";

export interface SyncPlannerInputs {
	workspaceName: string;
	documentId: string;
}

export interface SyncPlannerArgs extends SyncPlannerInputs {
	gitRef: ActorRefFrom<typeof gitRepositoryMachine>;
	authenticationActorRef: ActorRefFromLogic<AuthenticationMachine>;
}

export interface SyncPlannerContext extends SyncPlannerInputs {
	gitRef: ActorRefFrom<typeof gitRepositoryMachine>;
	authenticationActorRef: ActorRefFromLogic<AuthenticationMachine>;

	publishWorkspace: string | undefined;

	status: SyncPlanMaybe<GitRepoStatus | undefined>;
	branches: SyncPlanMaybe<GitBranchInfo[]>;
	remotes: SyncPlanMaybe<GitRemote[]>;

	remoteRepoExists: SyncPlanMaybe<boolean>;
	hasPublishPermission: SyncPlanMaybe<boolean>;
	isPublished: SyncPlanMaybe<boolean>;

	error: Error[];
}

const debuglog = (_msg) => {}; //console.log(`[syncPlanner] ${_msg}`);

export const syncPlannerMachine = setup({
	types: {
		input: {} as SyncPlannerArgs,
		context: {} as SyncPlannerContext,
		events: {} as GitRepositoryMachineEmitted,
		output: {} as {
			trySyncPlan: SyncPlan;
			publishIfUnpublishedPlan: SyncPlan;
			publishWorkspace: string;
		},
	},
	actors: {
		selectPublishWorkspace: fromPromise(
			async ({
				input,
			}: {
				input: {
					workspaceName: string;
					authenticationActorRef: ActorRefFromLogic<AuthenticationMachine>;
				};
			}) => {
				const authSnap = await waitFor(input.authenticationActorRef, authnStateNotUnknown);
				const publishWorkspace =
					input.workspaceName ||
					(authSnap.context.authnStatus === "authenticated" &&
						authSnap.context.user.nickname) ||
					"";
				return {
					isAuthenticated: authSnap.context.authnStatus === "authenticated",
					publishWorkspace,
				};
			}
		),
		momentRepoExists: fromPromise(remoteDocumentRepoExistsPromise),
		hasPublishPermission: fromPromise(hasPublishPermissionPromise),
		isPublished: fromPromise(isPublishedPromise),
	},
	actions: {
		sendGitStatus: sendTo(
			({ context }) => context.gitRef,
			// @ts-ignore self is an ActorRef but sender does not like it
			({ self }) => ({ type: "git.status", sender: self })
		),
		assignGitStatus: assign(({ event }) => ({
			status: event.output as GitRepoStatus | undefined,
		})),

		sendGitBranch: sendTo(
			({ context }) => context.gitRef,
			// @ts-ignore self is an ActorRef but sender does not like it
			({ self }) => ({ type: "git.branch.list", sender: self })
		),
		assignGitBranch: assign(({ event }) => ({
			branches: (event.output as GitBranchInfo[] | undefined) || [],
		})),

		sendGitRemote: sendTo(
			({ context }) => context.gitRef,
			// @ts-ignore self is an ActorRef but sender does not like it
			({ self }) => ({ type: "git.remote.list", sender: self })
		),
		assignGitRemote: assign(({ event }) => ({
			remotes: (event.output as GitRemote[] | undefined) || [],
		})),

		assignError: assign(({ context, event }) => ({
			error: [...context.error, (event["error"] as Error) || new Error("unknown error")],
		})),
		assignTimeoutError: assign(({ context }) => ({
			error: [...context.error, new Error("timed out")],
		})),
	},
}).createMachine({
	id: "syncPlannerMachine",
	context: ({ input }) => {
		return {
			...input,

			status: "unknown",
			branches: "unknown",
			remotes: "unknown",

			publishWorkspace: "unknown",

			remoteRepoExists: "unknown",
			hasPublishPermission: "unknown",
			isPublished: "unknown",

			error: [],
		};
	},
	initial: "querying",
	states: {
		querying: {
			type: "parallel",
			states: {
				checkingStatus: {
					initial: "querying",
					states: {
						querying: {
							entry: ["sendGitStatus", () => debuglog("sendGitStatus")],
							on: {
								"git.status.done": {
									actions: ["assignGitStatus", () => debuglog("gitStatusDone")],
									target: "statusDone",
								},
								"git.status.error": {
									actions: ["assignError", () => debuglog("gitStatusError")],
									target: "statusDone",
								},
							},
						},
						statusDone: {
							type: "final",
						},
					},
				},
				checkingTrackingBranch: {
					initial: "querying",
					states: {
						querying: {
							entry: ["sendGitBranch", () => debuglog("sendGitBranch")],
							on: {
								"git.branch.list.done": {
									actions: ["assignGitBranch", () => debuglog("gitBranchDone")],
									target: "branchDone",
								},
								"git.branch.list.error": {
									actions: ["assignError", () => debuglog("gitBranchError")],
									target: "branchDone",
								},
							},
						},
						branchDone: {
							type: "final",
						},
					},
				},
				checkingConfiguredRemote: {
					initial: "querying",
					states: {
						querying: {
							entry: ["sendGitRemote", () => debuglog("sendGitRemote")],
							on: {
								"git.remote.list.done": {
									actions: ["assignGitRemote", () => debuglog("gitRemoteDone")],
									target: "remoteDone",
								},
								"git.remote.list.error": {
									actions: ["assignError", () => debuglog("gitRemoteError")],
									target: "remoteDone",
								},
							},
						},
						remoteDone: {
							type: "final",
						},
					},
				},
				checkingMomentUpstream: {
					initial: "settingWorkspace",
					states: {
						settingWorkspace: {
							entry: [() => debuglog("settingWorkspace")],
							invoke: {
								src: "selectPublishWorkspace",
								input: ({ context }) => ({ ...context }),
								onDone: [
									{
										guard: ({ event }) => event.output.isAuthenticated,
										actions: [
											assign(({ event }) => ({
												publishWorkspace: event.output.publishWorkspace,
											})),
											() => debuglog("workspaceSetContinue"),
										],
										target: "queryingExistence",
									},
									{
										actions: [
											assign(({ event }) => ({
												publishWorkspace: event.output.publishWorkspace,
											})),
											() => debuglog("workspaceSetExit"),
										],
										target: "upstreamDone",
									},
								],
								onError: {
									target: "upstreamDone",
									actions: ["assignError", () => debuglog("workspaceError")],
								},
							},
							after: {
								1_000: {
									target: "upstreamDone",
									actions: [
										"assignTimeoutError",
										() => debuglog("workspaceTimeout"),
									],
								},
							},
						},
						queryingExistence: {
							entry: [() => debuglog("queryingExistence")],
							invoke: {
								src: "momentRepoExists",
								input: ({ context }) => ({ ...context }),
								onDone: [
									{
										guard: ({ event }) => event.output,
										actions: [
											assign(({ event }) => ({
												remoteRepoExists: event.output,
											})),
											() => debuglog("existenceConfirmed"),
										],
										target: "checkingPermissions",
									},
									{
										actions: [
											assign(({ event }) => ({
												remoteRepoExists: event.output,
											})),
											() => debuglog("existenceFailed"),
										],
										target: "upstreamDone",
									},
								],
								onError: {
									target: "upstreamDone",
									actions: ["assignError", () => debuglog("existenceError")],
								},
							},
							after: {
								1_000: {
									target: "upstreamDone",
									actions: [
										"assignTimeoutError",
										() => debuglog("existenceTimeout"),
									],
								},
							},
						},
						checkingPermissions: {
							type: "parallel",
							states: {
								checkingIsPublished: {
									initial: "queryIsPublished",
									states: {
										queryIsPublished: {
											entry: [() => debuglog("isPublished")],
											invoke: {
												src: "isPublished",
												input: ({ context }) => ({ ...context }),
												onDone: {
													actions: [
														assign(({ event }) => ({
															isPublished: event.output,
														})),
														() => debuglog("isPublishedChecked"),
													],
													target: "isPublishedDone",
												},
												onError: {
													target: "isPublishedDone",
													actions: [
														"assignError",
														() => debuglog("isPublishedError"),
													],
												},
											},
										},
										isPublishedDone: {
											type: "final",
										},
									},
								},
								checkingHasPublishPermission: {
									initial: "queryHasPublishPermission",
									states: {
										queryHasPublishPermission: {
											entry: [() => debuglog("hasPublishPermission")],
											invoke: {
												src: "hasPublishPermission",
												input: ({ context }) => ({ ...context }),
												onDone: {
													actions: [
														assign(({ event }) => ({
															hasPublishPermission: event.output,
														})),
														() =>
															debuglog("hasPublishPermissionChecked"),
													],
													target: "hasPublishPermissionDone",
												},
												onError: {
													target: "hasPublishPermissionDone",
													actions: [
														"assignError",
														() => debuglog("hasPublishPermissionError"),
													],
												},
											},
										},
										hasPublishPermissionDone: {
											type: "final",
										},
									},
								},
							},
							onDone: {
								target: "upstreamDone",
								actions: [() => debuglog("permissionsChecked")],
							},
							after: {
								1_000: {
									target: "upstreamDone",
									actions: [
										"assignTimeoutError",
										() => debuglog("permissionsTimeout"),
									],
								},
							},
						},
						upstreamDone: {
							type: "final",
						},
					},
				},
			},
			onDone: { target: "planning" },
		},
		planning: {
			type: "final",
		},
	},
	output: ({ context }) => {
		const authenticationSnap = context.authenticationActorRef.getSnapshot();
		const {
			publishWorkspace,

			status: gitRepoStatus,
			remotes,
			branches: trackingBranches,

			remoteRepoExists,
			hasPublishPermission,
			isPublished,
		} = context;

		debuglog("new SyncPlan calculated");
		return {
			trySyncPlan: syncPlan({
				syncMode: "try-sync",

				authenticationSnap,

				gitRepoStatus,
				remotes,
				trackingBranches,

				remoteRepoExists,
				hasPublishPermission,
				isPublished,
			}),
			publishIfUnpublishedPlan: syncPlan({
				syncMode: "publish-if-unpublished",

				authenticationSnap,

				gitRepoStatus,
				remotes,
				trackingBranches,

				remoteRepoExists,
				hasPublishPermission,
				isPublished,
			}),

			publishWorkspace: publishWorkspace ?? "",
		};
	},
});
